import React, { useState } from 'react';
import { StepProps, Specialty, SPECIALTY_COLORS } from '../../types';
import { motion, AnimatePresence } from 'framer-motion';
import { Droplets, Heart, FlaskConical, Stethoscope, ArrowRight, Eye, Zap } from 'lucide-react';
type PerceptionAnswer = 'Early / Proactive' | 'Balanced' | 'Late / Cautious';
interface SpecialtyQuestion {
  id: Specialty;
  icon: React.ElementType;
  question: string;
  stateKey: 'perceptionNeph' | 'perceptionCardio' | 'perceptionEndo' | 'perceptionPCP';
}
const questions: SpecialtyQuestion[] = [{
  id: 'Nephrologist',
  icon: Droplets,
  question: 'How do you think nephrologists typically approach CKD treatment?',
  stateKey: 'perceptionNeph'
}, {
  id: 'Cardiologist',
  icon: Heart,
  question: 'When do cardiologists usually initiate CKD treatment?',
  stateKey: 'perceptionCardio'
}, {
  id: 'Endocrinologist',
  icon: FlaskConical,
  question: 'How proactive are endocrinologists in managing CKD?',
  stateKey: 'perceptionEndo'
}, {
  id: 'PCP',
  icon: Stethoscope,
  question: 'How do PCPs typically handle CKD management?',
  stateKey: 'perceptionPCP'
}];
const answerOptions: PerceptionAnswer[] = ['Early / Proactive', 'Balanced', 'Late / Cautious'];
export function PerceptionCapture({
  state,
  updateState,
  nextStep
}: StepProps) {
  const [phase, setPhase] = useState<'choice' | 'questions'>(state.perceptionMode ? 'questions' : 'choice');
  const [currentQ, setCurrentQ] = useState(0);
  const handleChooseDirect = () => {
    updateState({
      perceptionMode: false
    });
    nextStep();
  };
  const handleChoosePerception = () => {
    updateState({
      perceptionMode: true
    });
    setPhase('questions');
  };
  // Questions phase logic
  const q = questions[currentQ];
  const Icon = q.icon;
  const color = SPECIALTY_COLORS[q.id];
  const currentAnswer = state[q.stateKey] as PerceptionAnswer | null;
  const handleAnswer = (answer: PerceptionAnswer) => {
    updateState({
      [q.stateKey]: answer
    });
  };
  const handleNext = () => {
    if (currentQ < questions.length - 1) {
      setCurrentQ(prev => prev + 1);
    } else {
      nextStep();
    }
  };
  // CHOICE SCREEN
  if (phase === 'choice') {
    return <div className="flex-grow flex flex-col justify-center items-center px-6 py-12" data-id="element-298">
        <div className="max-w-2xl w-full mx-auto text-center" data-id="element-299">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} data-id="element-300">
            <h2 className="text-3xl md:text-5xl font-bold text-dark mb-4" data-id="element-301">
              How would you like to explore?
            </h2>
            <p className="text-lg text-gray-500 mb-12" data-id="element-302">
              Choose your path through the specialty landscape.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-303">
            <motion.button initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.2
          }} whileHover={{
            scale: 1.02,
            y: -4
          }} whileTap={{
            scale: 0.98
          }} onClick={handleChooseDirect} className="bg-white border-2 border-gray-200 hover:border-gray-300 rounded-3xl p-8 text-left transition-colors group" data-id="element-304">
              <div className="w-14 h-14 rounded-2xl bg-gray-100 flex items-center justify-center mb-6 group-hover:bg-gray-200 transition-colors" data-id="element-305">
                <Zap size={28} className="text-gray-600" data-id="element-306" />
              </div>
              <h3 className="text-xl font-bold text-dark mb-2" data-id="element-307">
                Explore insights directly
              </h3>
              <p className="text-gray-500 leading-relaxed" data-id="element-308">
                Jump straight into specialty profiles, data, and analysis. Fast
                and efficient.
              </p>
            </motion.button>

            <motion.button initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.3
          }} whileHover={{
            scale: 1.02,
            y: -4
          }} whileTap={{
            scale: 0.98
          }} onClick={handleChoosePerception} className="bg-white border-2 border-brand/30 hover:border-brand/50 rounded-3xl p-8 text-left transition-colors group" data-id="element-309">
              <div className="w-14 h-14 rounded-2xl bg-brand/10 flex items-center justify-center mb-6 group-hover:bg-brand/15 transition-colors" data-id="element-310">
                <Eye size={28} className="text-brand" data-id="element-311" />
              </div>
              <h3 className="text-xl font-bold text-dark mb-2" data-id="element-312">
                Test your assumptions first
              </h3>
              <p className="text-gray-500 leading-relaxed" data-id="element-313">
                Predict how each specialty behaves, then see how your
                assumptions compare to reality.
              </p>
              <span className="inline-block mt-3 text-xs font-bold uppercase tracking-wider text-brand bg-brand/10 px-3 py-1 rounded-full" data-id="element-314">
                Recommended
              </span>
            </motion.button>
          </div>
        </div>
      </div>;
  }
  // QUESTIONS SCREEN (only shown if perception mode)
  return <div className="flex-grow flex flex-col justify-center items-center px-6 py-12" data-id="element-315">
      <div className="max-w-3xl w-full mx-auto" data-id="element-316">
        <div className="text-center mb-10" data-id="element-317">
          <div className="inline-flex items-center justify-center p-3 bg-gray-100 rounded-full mb-4" data-id="element-318">
            <Eye size={28} className="text-gray-600" data-id="element-319" />
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-dark mb-3" data-id="element-320">
            Test Your Assumptions
          </h2>
          <p className="text-lg text-gray-500" data-id="element-321">
            Tell us what you <em data-id="element-322">think</em> happens in practice.
          </p>
        </div>

        {/* Progress dots */}
        <div className="flex justify-center gap-2 mb-10" data-id="element-323">
          {questions.map((_, i) => <div key={i} className={`w-3 h-3 rounded-full transition-all duration-300 ${i === currentQ ? 'scale-125' : i < currentQ ? 'opacity-100' : 'bg-gray-200'}`} style={{
          backgroundColor: i === currentQ ? SPECIALTY_COLORS[questions[i].id] : i < currentQ ? SPECIALTY_COLORS[questions[i].id] : undefined,
          opacity: i < currentQ ? 0.4 : undefined
        }} data-id="element-324" />)}
        </div>

        <AnimatePresence mode="wait" data-id="element-325">
          <motion.div key={currentQ} initial={{
          opacity: 0,
          x: 40
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: -40
        }} transition={{
          duration: 0.35
        }} data-id="element-326">
            <div className="flex items-center justify-center mb-8" data-id="element-327">
              <div className="p-3 rounded-full mr-4" style={{
              backgroundColor: `${color}15`,
              color
            }} data-id="element-328">
                <Icon size={28} data-id="element-329" />
              </div>
              <span className="text-xl font-bold" style={{
              color
            }} data-id="element-330">
                {q.id}s
              </span>
            </div>

            <h3 className="text-2xl font-bold text-center mb-8 text-dark" data-id="element-331">
              {q.question}
            </h3>

            <div className="space-y-3 mb-10" data-id="element-332">
              {answerOptions.map(opt => {
              const isSelected = currentAnswer === opt;
              return <motion.button key={opt} whileHover={{
                scale: 1.01
              }} whileTap={{
                scale: 0.99
              }} onClick={() => handleAnswer(opt)} className={`w-full p-5 rounded-2xl border-2 text-left flex items-center justify-between transition-all duration-200 ${isSelected ? 'bg-gray-50' : 'bg-white border-gray-200 hover:border-gray-300'}`} style={{
                borderColor: isSelected ? color : undefined
              }} data-id="element-333">
                    <span className={`text-lg font-medium ${isSelected ? 'text-dark' : 'text-gray-600'}`} data-id="element-334">
                      {opt}
                    </span>
                    <div className="w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0" style={{
                  borderColor: isSelected ? color : '#d1d5db',
                  backgroundColor: isSelected ? color : 'transparent'
                }} data-id="element-335">
                      {isSelected && <div className="w-2 h-2 bg-white rounded-full" data-id="element-336" />}
                    </div>
                  </motion.button>;
            })}
            </div>
          </motion.div>
        </AnimatePresence>

        <div className="flex justify-center h-14" data-id="element-337">
          {currentAnswer && <motion.button initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} onClick={handleNext} className="inline-flex items-center justify-center px-8 py-3 text-base font-semibold text-white bg-dark rounded-full hover:bg-black transition-colors" data-id="element-338">
              {currentQ < questions.length - 1 ? 'Next Specialty' : 'Continue'}
              <ArrowRight className="ml-2 w-5 h-5" data-id="element-339" />
            </motion.button>}
        </div>
      </div>
    </div>;
}